// BIM coordination dashboard — /coordination/:projectId
//
// Top → bottom: 6-week SVG gantt (components/CoordinationGantt),
// issues due today, schedule-health stats row (+ behind/at-risk mini
// table), awaiting-triage banner. Row click opens a Drawer with the
// item's checklist, dates, status, and its issues.
//
// Data:
//   GET /projects/:projectId/gantt   (items + issues + health)
//   GET /projects                    (title lookup, ApprovalQueue pattern)
import { PartnerWithTrades } from "components/Partners/PartnerTradeLabel";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { Row, Col, Card, Table, Tag, Button, Drawer, Empty, Input, Skeleton, message, Alert, Dropdown, Modal, Select, Tabs, Popconfirm } from "antd";
import { ScheduleOutlined, TeamOutlined, ReloadOutlined, PrinterOutlined, DownOutlined, FileExcelOutlined, FilePdfOutlined, FileTextOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";
import IssueExportMenu from "components/Issue/IssueExportMenu";
import ProjectHoursCard from "components/Coordination/ProjectHoursCard";
import FieldProgressRollup from "components/Coordination/FieldProgressRollup";
import GlanceTab from "components/Coordination/Glance/GlanceTab";
import { useSelector } from "react-redux";
import { getGanttFeed, claimGanttItem, exportSchedule } from "api/gantt/ganttApi";
import CoordinationGantt from "components/CoordinationGantt/CoordinationGantt";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import ReportingCatchUp from "components/Coordination/ReportingCatchUp";
import ActivityShopDrawings from "components/Coordination/ActivityShopDrawings";
import useProjectAccess from "hooks/useProjectAccess";
import { rememberProject } from "helpers/projectSelection";
import IssueAnalyticsChart from "pages/Issue/components/IssueAnalyticsChart";
import { issueLabel, labelFor } from "helpers/artifactPrefix";
import { isMeeting } from "helpers/schedule/activityTypes";
import { pctDisplay, PARENT_TIP, progressEditRule } from "components/CoordinationGantt/ProgressCell";
import { activityLabel, activityTitle, withActivityPaths } from "helpers/schedule/activityLabel";
import { issueOnActivity } from "helpers/schedule/issueActivityIds";
import { matchOption } from "components/SearchSelect/searchSelect";

const HEALTH_COLORS = {
  behind: "red",
  at_risk: "orange",
  on_track: "green",
};

const PRIORITY_COLORS = { urgent: "red", normal: "blue", low: "default" };

const OPENISH = ["open", "pending", "under_review"];

function auth() {
  return { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` };
}

// Cheap project-title lookup — same pattern as ApprovalQueue.jsx
async function fetchProjects() {
  const res = await fetch(`${url}/projects/picker`, { headers: auth() });
  if (!res.ok) return [];
  const body = await res.json();
  return Array.isArray(body) ? body : (body?.projects || []);
}

const fmtDate = (v) => (v ? String(v).slice(0, 10) : "-");

// Minutes/attendance for a recurring meeting item — shown in the item
// drawer. Upserts per occurrence day (PUT /projects/:id/minutes).
function MeetingMinutesSection({ projectId, item, canWrite = false, canDelete = false }) {
  const [rows, setRows] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [attendees, setAttendees] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    fetch(`${url}/projects/${projectId}/minutes?ganttItemId=${item._id}`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => setRows(Array.isArray(list) ? list : []))
      .catch(() => {});
  };
  useEffect(() => { load(); }, [item._id]); // eslint-disable-line

  // Editing an existing occurrence pre-fills the form.
  useEffect(() => {
    const existing = rows.find((r) => String(r.occurrenceDate).slice(0, 10) === date);
    setNotes(existing?.notes || "");
    setAttendees((existing?.attendees || []).join(", "));
  }, [date, rows]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${url}/projects/${projectId}/minutes`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({
          ganttItemId: item._id,
          occurrenceDate: date,
          notes,
          attendees: attendees.split(",").map((a) => a.trim()).filter(Boolean),
        }),
      });
      if (!res.ok) throw new Error((await res.json()).err || `HTTP ${res.status}`);
      message.success("Minutes saved");
      load();
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  // One occurrence's minutes, removed (minutesDelete, 2026-10-14).
  const remove = async (r) => {
    try {
      const day = String(r.occurrenceDate).slice(0, 10);
      const res = await fetch(`${url}/projects/${projectId}/minutes?ganttItemId=${item._id}&occurrenceDate=${day}`, {
        method: "DELETE", headers: auth(),
      });
      if (!res.ok) throw new Error((await res.json()).err || `HTTP ${res.status}`);
      message.success(`Minutes for ${day} deleted`);
      load();
    } catch (e) {
      message.error(e.message);
    }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <h4 style={{ marginBottom: 6 }}>Meeting minutes</h4>
      {/* Owner-company side with minutesAdd / minutesEdit writes; everyone
          else (partner companies) reads the saved occurrences below. */}
      {canWrite ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <input type="date" value={date}
                 style={{ border: "1px solid #d9d9d9", borderRadius: 4, padding: "3px 8px", width: 160 }}
                 onChange={(e) => setDate(e.target.value)} />
          <Input.TextArea rows={3} placeholder="Notes / decisions / actions"
                          value={notes} onChange={(e) => setNotes(e.target.value)} />
          <Input placeholder="Attendees (comma-separated)" value={attendees}
                 onChange={(e) => setAttendees(e.target.value)} />
          <Button size="small" type="primary" loading={saving} onClick={save}
                  style={{ alignSelf: "flex-start" }}>
            Save minutes
          </Button>
        </div>
      ) : (
        rows.length === 0 && <div style={{ color: "#999" }}>No minutes yet</div>
      )}
      {rows.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {rows.slice(0, 8).map((r) => (
            <div key={r._id}
                 style={{ padding: "4px 0", borderBottom: "1px solid #f5f5f5", cursor: "pointer" }}
                 onClick={() => setDate(String(r.occurrenceDate).slice(0, 10))}>
              <b>{String(r.occurrenceDate).slice(0, 10)}</b>
              {r.attendees?.length ? <span style={{ color: "#888" }}> · {r.attendees.length} attendee(s)</span> : null}
              {canDelete ? (
                <span onClick={(e) => e.stopPropagation()} style={{ float: "right" }}>
                  <Popconfirm title={`Delete the minutes of ${String(r.occurrenceDate).slice(0, 10)}?`}
                              okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => remove(r)}>
                    <Button size="small" type="link" danger data-testid="minutes-delete">Delete</Button>
                  </Popconfirm>
                </span>
              ) : null}
              <div style={{ color: "#666", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {r.notes}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BimDashboardPage() {
  const { projectId } = useParams();
  const history = useHistory();
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  // Owner-vs-partner + CRUD keys resolved server-side (GET
  // /projects/:id/access): drag editing only for owner-company users with
  // ganttEdit; partner companies (their companyAdmin included) get the
  // plan read-only, and the progress popover only with ganttProgressReport
  // on their own items.
  const { can, canEditPlan, isOwnerCompany, access } = useProjectAccess(projectId);
  // Partner companies for the per-contractor print report.
  const [partnerRoster, setPartnerRoster] = useState([]);
  useEffect(() => {
    if (!projectId) return;
    fetch(`${url}/projects/${projectId}/participants/roster`, { headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setPartnerRoster((Array.isArray(rows) ? rows : []).filter((g) => g.role !== "owner")))
      .catch(() => setPartnerRoster([]));
  }, [projectId]);
  // THE SCHEDULE AS A FILE (owner, 2026-10-20: "add the same pdf, excel, csv
  // export to schedule"): every activity in it, each column, beside the print
  // pages above. The server scopes a partner to their portion; `company`
  // narrows an owner's file to one contractor.
  const [scheduleExporting, setScheduleExporting] = useState("");
  const downloadSchedule = async (ext, company) => {
    const NAMES = { pdf: "PDF", xlsx: "Excel", csv: "CSV" };
    setScheduleExporting(ext);
    try {
      const r = await exportSchedule(projectId, ext, { company });
      message.success(`${NAMES[ext]} downloaded - ${r.count} activit${r.count === 1 ? "y" : "ies"}`);
    } catch (e) {
      message.error(e.message || `${NAMES[ext]} export failed`);
    } finally {
      setScheduleExporting("");
    }
  };
  const scheduleFileItems = (prefix, what, company) => [
    { key: `${prefix}-pdf`, icon: <FilePdfOutlined />, label: `PDF download - ${what} (activity table)`, onClick: () => downloadSchedule("pdf", company) },
    { key: `${prefix}-xlsx`, icon: <FileExcelOutlined />, label: `Excel - ${what} (every column, outline, relationships)`, onClick: () => downloadSchedule("xlsx", company) },
    { key: `${prefix}-csv`, icon: <FileTextOutlined />, label: `CSV - ${what} (one row per activity)`, onClick: () => downloadSchedule("csv", company) },
  ];
  const canEditGantt = !!can.ganttEdit;
  // A partner whose admin switched "Edit their plan rows" on may drag the
  // bars their OWN company is responsible for. The server has always
  // allowed exactly that (routes/ganttItems.js) and refuses the rest, so
  // the chart hands it the same rule rather than a looser one: the whole
  // plan stays visible, and only their rows have a grab cursor.
  const myCompany = String(userInfoRedux?.company || "");
  const ganttEditable = canEditGantt
    ? true
    : (can.ganttEditOwnRows
        ? (item) => !!item?.responsibleCompanyId && String(item.responsibleCompanyId) === myCompany
        : false);
  // Arriving here (deep link, breadcrumb, landing) selects this project for
  // the whole coordination suite - remember it.
  useEffect(() => { if (projectId) rememberProject(userInfoRedux?._id || userInfoRedux?.id, projectId); }, [projectId]); // eslint-disable-line

  const [feed, setFeed] = useState(null);
  // Claiming unassigned work (partner side). The area is asked for at
  // claim time because claiming is how an activity gets filed into the
  // area breakdown - see back/routes/ganttItems POST /:id/claim.
  const [claimTarget, setClaimTarget] = useState(null);
  const [claimArea, setClaimArea] = useState("");
  const [claimNote, setClaimNote] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [loading, setLoading] = useState(true);
  const [projectTitle, setProjectTitle] = useState("");
  const [selected, setSelected] = useState(null); // gantt item for the drawer
  // ?focus=<ganttItemId> (digest / notification links): open that activity's
  // drawer - its issues, checklist and minutes - once the feed is in.
  const location = useLocation();
  const focusId = (() => { try { return new URLSearchParams(location.search).get("focus") || ""; } catch (e) { return ""; } })();
  const focusedOnce = useRef(false);
  // Tabs: "At a Glance" sits first, but a project opens on the dashboard
  // (the owner's call). ?tab= keeps the choice in the URL.
  const tabFromUrl = (() => { try { return new URLSearchParams(location.search).get("tab") || ""; } catch (e) { return ""; } })();
  const activeTab = tabFromUrl === "glance" ? "glance" : "dashboard";
  const setTab = (key) => {
    const params = new URLSearchParams(location.search);
    params.set("tab", key);
    history.replace({ pathname: location.pathname, search: `?${params.toString()}` });
  };
  const [showIssueAnalytics, setShowIssueAnalytics] = useState(false);

  const load = () => {
    setLoading(true);
    getGanttFeed(projectId)
      .then(setFeed)
      .catch((e) => message.error(e.message || "Failed to load coordination feed"))
      .finally(() => setLoading(false));
  };

  // Live updates: SSE stream of this project's issue events (created /
  // status / priority / comment) — debounced refetch keeps the gantt
  // badges, due-today table and health cards current without polling.
  useEffect(() => {
    if (!projectId) return;
    const token = localStorage.getItem(tokenRef);
    let timer = null;
    let es;
    try {
      es = new EventSource(
        `${url}/issue/events?projectId=${projectId}&token=${encodeURIComponent(token || "")}`
      );
      es.onmessage = () => {
        clearTimeout(timer);
        timer = setTimeout(() => load(), 1500);
      };
    } catch (e) { /* SSE unsupported → manual refresh still works */ }
    return () => {
      clearTimeout(timer);
      if (es) es.close();
    };
  }, [projectId]); // eslint-disable-line

  useEffect(() => {
    load();
    fetchProjects().then((projects) => {
      const p = projects.find((x) => x._id === projectId);
      if (p) setProjectTitle(p.title);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Ancestor names added here, on the WHOLE feed, before anything filters
  // it: every list below is a filter of this one, and an ancestor a filter
  // removed is an ancestor the walk cannot resolve.
  const items = useMemo(() => withActivityPaths(feed?.items || []), [feed]);
  const issues = feed?.issues || [];
  useEffect(() => {
    if (!focusId || focusedOnce.current || !items.length) return;
    const it = items.find((i) => String(i._id) === String(focusId));
    if (!it) return;
    focusedOnce.current = true;
    setSelected(it);
  }, [focusId, items]); // eslint-disable-line
  const health = feed?.health || {};
  const counts = health.counts || {};

  // ---- Issues due today (overdue first) --------------------------------
  const dueToday = useMemo(() => {
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return issues
      .filter(
        (i) =>
          OPENISH.includes(i.status) &&
          i.dueDate &&
          new Date(i.dueDate) <= endOfToday
      )
      .map((i) => ({
        ...i,
        overdue: new Date(i.dueDate) < startOfToday,
      }))
      .sort((a, b) => {
        if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
        return new Date(a.dueDate) - new Date(b.dueDate);
      });
  }, [issues]);

  const triageCount = useMemo(
    () => issues.filter((i) => i.needsTriage).length,
    [issues]
  );

  const problemItems = useMemo(
    () =>
      items.filter(
        (i) => i.healthStatus === "behind" || i.healthStatus === "at_risk"
      ),
    [items]
  );

  const drawerIssues = useMemo(() => {
    if (!selected) return [];
    return issues.filter(
      (i) => issueOnActivity(i, selected._id)
    );
  }, [selected, issues]);

  // Open the issue's Resolve page (it shows view or edit controls by the
  // viewer's own permission); straight to the share route when the feed
  // carries the token, else via the id -> token redirect.
  const issueLink = (i) => history.push(i?.shareToken ? `/issue/${i.shareToken}/resolve` : `/issue-viewer/${i._id}`);

  const dueColumns = [
    {
      title: "ID",
      dataIndex: "displayId",
      width: 90,
      render: (v, r) => (
        <a onClick={() => issueLink(r)}>{v ? labelFor(v, r.artifactPrefix) : "—"}</a>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      render: (v, r) => (
        <span>
          <a onClick={() => issueLink(r)}>{v}</a>
          {r.overdue && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              overdue
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      width: 100,
      render: (v) =>
        v ? <Tag color={PRIORITY_COLORS[v] || "default"}>{v}</Tag> : "-",
    },
    { title: "Due", dataIndex: "dueDate", width: 120, render: fmtDate },
    {
      title: "Health",
      dataIndex: "health",
      width: 110,
      render: (v) =>
        v ? <Tag color={HEALTH_COLORS[v] || "default"}>{v}</Tag> : "-",
    },
  ];

  const problemColumns = [
    // A FLAT list of at-risk activities is the exact shape the owner
    // complained about - the same leaf name three times with nothing to
    // choose between them (2026-09-29).
    { title: "Item", dataIndex: "name", render: (_, r) => <span title={activityTitle(r)}>{activityLabel(r)}</span> },
    { title: "End", dataIndex: "endDate", width: 120, render: fmtDate },
    {
      title: "Health",
      dataIndex: "healthStatus",
      width: 110,
      render: (v) => <Tag color={HEALTH_COLORS[v] || "default"}>{v}</Tag>,
    },
    {
      title: "Open issues",
      dataIndex: "issueCounts",
      width: 220,
      render: (c) =>
        c
          ? `${c.open || 0} open (${c.open_behind || 0} behind, ${c.open_at_risk || 0} at risk)`
          : "-",
    },
  ];

  return (
    <div style={{ padding: 24, fontFamily: "Inter, system-ui, sans-serif", maxWidth: 1300, margin: "0 auto" }}>
      <CoordinationBreadcrumbs current={projectTitle || "Dashboard & Schedule"} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h2 style={{ margin: 0 }}>BIM Coordination Dashboard</h2>
          <div style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
            The plan as of today, what is due, and where the schedule hurts.
          </div>
          {/* THE SAME NUMBER AS THE SCHEDULE EDITOR AND THE DIGESTS. It
              belongs on the dashboard because this is the page whose figures
              it qualifies - "what is due" is only as true as the records are
              current. One component, three places, one sentence. */}
          <ReportingCatchUp projectId={projectId} compact />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refresh
          </Button>
          {canEditPlan && (
            <Button
              icon={<ScheduleOutlined />}
              onClick={() => history.push(`/scheduler/${projectId}`)}
            >
              Schedule
            </Button>
          )}
          <Button
            icon={<TeamOutlined />}
            onClick={() => history.push(`/partners?projectId=${projectId}`)}
          >
            Partners
          </Button>
          {/* Whole-project export: BCF 2.1 or the PDF register (shared
              component with the Issues view, which exports its filters). */}
          <IssueExportMenu label="Export BCF / PDF / Excel / CSV" filters={{ projectId }} />
          {/* Estimates are the owner company's pricing (guide 21): never shown to partners. */}
          {isOwnerCompany ? (
            <Button onClick={() => history.push(`/coordination/${projectId}/estimates`)}>Estimates</Button>
          ) : null}
          <Dropdown
            menu={{
              // Partners get only their own portion (the server scopes the
              // feed to their company; the pages lock the scope picker).
              items: !isOwnerCompany ? [
                { key: "my-gantt", label: "Gantt chart - my company's portion (wide sheet)", onClick: () => window.open(`/gantt-print/${projectId}`, "_blank") },
                { key: "my-report", label: "Schedule report - my company's portion", onClick: () => window.open(`/schedule-print/${projectId}`, "_blank") },
                { type: "divider" },
                ...scheduleFileItems("my", "my company's portion"),
              ] : [
                { key: "gantt", label: "Gantt chart - whole project (wide sheet, every column + timeline)", onClick: () => window.open(`/gantt-print/${projectId}`, "_blank") },
                { key: "all", label: "Schedule report - whole project (activities with issues, checklists, relationships)", onClick: () => window.open(`/schedule-print/${projectId}`, "_blank") },
                { type: "divider" },
                ...scheduleFileItems("all", "whole project"),
                ...(partnerRoster.length ? [{ type: "divider" }] : []),
                ...partnerRoster.map((g) => ({
                  key: g.companyId, label: <PartnerWithTrades name={`${g.companyName} only`} trades={(g.tradeLabels && g.tradeLabels.length ? g.tradeLabels : g.trades) || []} />,
                  children: [
                    { key: `${g.companyId}-gantt`, label: "Gantt chart (their portion)", onClick: () => window.open(`/gantt-print/${projectId}?company=${g.companyId}`, "_blank") },
                    { key: `${g.companyId}-report`, label: "Schedule report (their portion)", onClick: () => window.open(`/schedule-print/${projectId}?company=${g.companyId}`, "_blank") },
                    ...scheduleFileItems(g.companyId, "their portion", g.companyId),
                  ],
                })),
              ],
            }}
            trigger={["click"]}
          >
            <Button icon={<PrinterOutlined />} title="Print-ready schedule report (Save as PDF): activities, milestones, meetings with their issues, actions, checklists and relationships - whole project or one contractor's portion">
              {scheduleExporting ? "Building…" : "Print / PDF / Excel / CSV"} <DownOutlined />
            </Button>
          </Dropdown>
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setTab}
        destroyInactiveTabPane={false}
        items={[
          {
            key: "glance",
            label: "At a Glance",
            children: (
              <GlanceTab
                projectId={projectId}
                // A schedule milestone on the timeline opens that activity's
                // drawer right here; the schedule fact switches tabs.
                onOpenDashboard={(ganttItemId) => {
                  const it = ganttItemId && items.find((i) => String(i._id) === String(ganttItemId));
                  if (it) setSelected(it); else setTab("dashboard");
                }}
              />
            ),
          },
          {
            key: "dashboard",
            label: "Dashboard & Schedule",
            children: (
              <>
              {loading && !feed ? (
                <Skeleton active paragraph={{ rows: 8 }} />
              ) : (
                <>
                  {/* c. Stats row */}
                  <h3 style={{ marginBottom: 8 }}>Schedule health</h3>
                  <Row gutter={12} style={{ marginBottom: 12 }}>
                    <Col xs={12} md={4}>
                      <Card size="small">
                        <div style={{ color: "#888", fontSize: 12 }}>Open issues</div>
                        <div style={{ fontSize: 22, fontWeight: 700 }}>{counts.open || 0}</div>
                      </Card>
                    </Col>
                    <Col xs={12} md={5}>
                      <Card size="small">
                        <div style={{ color: "#888", fontSize: 12 }}>Resolved on time</div>
                        <div style={{ fontSize: 22, fontWeight: 700 }}>
                          {health.resolvedOnTimePct == null ? "—" : `${health.resolvedOnTimePct}%`}
                        </div>
                      </Card>
                    </Col>
                    <Col xs={12} md={5}>
                      <Card size="small">
                        <div style={{ color: "#888", fontSize: 12 }}>Behind</div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: (health.behindItems || 0) > 0 ? "#d4380d" : undefined }}>
                          {health.behindItems || 0} item{(health.behindItems || 0) === 1 ? "" : "s"}
                        </div>
                      </Card>
                    </Col>
                    <Col xs={12} md={5}>
                      <Card size="small">
                        <div style={{ color: "#888", fontSize: 12 }}>At risk</div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: (health.atRiskItems || 0) > 0 ? "#d48806" : undefined }}>
                          {health.atRiskItems || 0}
                        </div>
                      </Card>
                    </Col>
                    <Col xs={12} md={5}>
                      <Card size="small">
                        <div style={{ color: "#888", fontSize: 12 }}>Untagged open issues</div>
                        <div style={{ fontSize: 22, fontWeight: 700 }}>
                          {health.untaggedOpenIssues || 0}
                        </div>
                      </Card>
                    </Col>
                  </Row>
                  {/* a. Gantt as of today */}
                  <h3 style={{ margin: "16px 0 8px" }}>Gantt - whole plan (Ctrl + wheel to zoom, scroll left/right)</h3>
                  <CoordinationGantt
                    items={items}
                    projectId={projectId}
                    issues={feed?.issues || []}
                    checklistItems={feed?.checklistItems || []}
                    areasByItem={feed?.areasByItem || {}}
                    onSelect={setSelected}
                    editable={ganttEditable}
                    onEditItem={canEditGantt ? (item) => history.push(`/scheduler/${projectId}?focus=${item._id}`) : undefined}
                    onChanged={load}
                    canEditProgress={progressEditRule(can, myCompany)}
                    canReportProgress={(item) =>
                      !!can.ganttProgressReport &&
                      !!item?.responsibleCompanyId &&
                      String(item.responsibleCompanyId) === String(userInfoRedux?.company || "")
                    }
                    onClaim={(item) => {
                      setClaimTarget(item);
                      setClaimArea("");
                      setClaimNote("");
                    }}
                  />

                  {/* b. Issues due today */}
                  <h3 style={{ margin: "24px 0 8px" }}>Issues due today</h3>
                  <Table
                    size="small"
                    dataSource={dueToday}
                    columns={dueColumns}
                    rowKey="_id"
                    onRow={(r) => ({ onClick: () => issueLink(r), style: { cursor: "pointer" }, title: "Open this issue" })}
                    pagination={false}
                    locale={{ emptyText: <Empty description="Nothing due today 🎉" /> }}
                  />

                  {problemItems.length > 0 && (
                    <Table
                      size="small"
                      dataSource={problemItems}
                      columns={problemColumns}
                      rowKey="_id"
                      onRow={(r) => ({ onClick: () => setSelected(r), style: { cursor: "pointer" }, title: "Open this activity" })}
                      pagination={false}
                      style={{ marginBottom: 12 }}
                    />
                  )}

                  {/* d. Awaiting triage - owner-side only: partners (non-owner
                      companies) get the schedule / overdue / issues here but never
                      the Review Center hand-off. */}
                  {isOwnerCompany && triageCount > 0 && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginTop: 12 }}
                      message={`${triageCount} partner-created issue${triageCount === 1 ? "" : "s"} awaiting triage`}
                      action={
                        <Button
                          size="small"
                          onClick={() => history.push(`/approval-queue?projectId=${projectId}`)}
                        >
                          Open Review Center
                        </Button>
                      }
                    />
                  )}

                  {/* The FIELD's lane: counted from tracked model elements,
                      by package or by area. Issues (BIM) and checklists
                      (management) are shown apart - owner's three lanes. */}
                  <FieldProgressRollup projectId={projectId} />

                  {/* Issue analytics (Merge D): the same recharts panel the
                      Issues page uses, project-scoped — status/priority pies,
                      30-day trend, per-project bars, top creators, resolution
                      stats. Collapsed behind a toggle to keep the schedule
                      view above the fold. */}
                  <Card
                    size="small"
                    style={{ marginTop: 16 }}
                    title="Issue analytics"
                    extra={
                      <Button size="small" onClick={() => setShowIssueAnalytics((v) => !v)}>
                        {showIssueAnalytics ? "Hide" : "Show"}
                      </Button>
                    }
                  >
                    {showIssueAnalytics && <IssueAnalyticsChart projectId={projectId} />}
                  </Card>
                  {/* Hours spent on issues and their cost - owner / company admins only. */}
                  {access && access.canSeeHours ? <ProjectHoursCard projectId={projectId} /> : null}
                </>
              )}
              </>
            ),
          },
        ]}
      />

      {/* Drawer — selected gantt item detail */}
      {/* Claim an unassigned activity. The area is part of the claim, not
          a later cleanup: the trade doing the work is the one who knows
          where it happens, and the server files the activity into the
          area breakdown when the owner approves. */}
      <Modal
        title={claimTarget ? `Claim "${claimTarget.name}"` : ""}
        open={!!claimTarget}
        onCancel={() => setClaimTarget(null)}
        okText="Send to the project owner"
        confirmLoading={claiming}
        onOk={async () => {
          if (!claimTarget) return;
          setClaiming(true);
          try {
            await claimGanttItem(projectId, claimTarget._id, {
              areaId: claimArea || undefined,
              note: claimNote.trim(),
            });
            message.success("Claim sent - the project owner reviews it before it moves");
            setClaimTarget(null);
            load();
          } catch (e) {
            if (e?.needsArea) message.error("Pick the area this work happens in");
            else message.error(e?.message || "Claim failed");
          } finally {
            setClaiming(false);
          }
        }}
        destroyOnClose
      >
        <div style={{ color: "#666", fontSize: 13, marginBottom: 10 }}>
          Nobody is assigned to this activity. Claiming asks the project owner
          to hand it to your company — nothing changes on the schedule until
          they approve it.
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
          Which area is this work in?
        </div>
        <Select filterOption={matchOption}
          style={{ width: "100%", marginBottom: 12 }}
          placeholder={(feed?.areas || []).length ? "Pick the area" : "No areas drawn on this project yet"}
          disabled={!(feed?.areas || []).length}
          value={claimArea || undefined}
          onChange={setClaimArea}
          showSearch
          optionFilterProp="children"
        >
          {(feed?.areas || []).map((a) => (
            <Select.Option key={a._id} value={String(a._id)}>
              {a.name}{a.code ? ` · ${a.code}` : ""}
            </Select.Option>
          ))}
        </Select>
        <Input.TextArea
          rows={3}
          placeholder="Anything the owner should know (optional)"
          value={claimNote}
          onChange={(e) => setClaimNote(e.target.value)}
        />
      </Modal>

      <Drawer
        title={selected ? activityLabel(selected) : ""}
        open={!!selected}
        onClose={() => setSelected(null)}
        width={420}
      >
        {selected && (
          <div style={{ fontSize: 13 }}>
            <p style={{ marginBottom: 6 }}>
              <Tag>{selected.type}</Tag>
              {selected.status && <Tag>{selected.status}</Tag>}
              {selected.healthStatus && (
                <Tag color={HEALTH_COLORS[selected.healthStatus] || "default"}>
                  {selected.healthStatus}
                </Tag>
              )}
              {(() => {
                // The row as the feed has it now (an inline % edit reloads the
                // feed; `selected` is the object that was clicked).
                const live = (items || []).find((i) => String(i._id) === String(selected._id)) || selected;
                const parent = (items || []).some((i) => String(i.parentId || "") === String(selected._id));
                return (
                  <Tag title={parent ? PARENT_TIP : undefined}>
                    {pctDisplay(live, parent).unknown ? "no data entered" : pctDisplay(live, parent).text}{parent && !pctDisplay(live, parent).unknown ? ` · ${PARENT_TIP.toLowerCase()}` : ""}
                  </Tag>
                );
              })()}
            </p>
            <p style={{ marginBottom: 12, color: "#666" }}>
              {fmtDate(selected.startDate)} → {fmtDate(selected.endDate)}
            </p>

            {isMeeting(selected.type) && (
              <MeetingMinutesSection projectId={projectId} item={selected}
                                     canWrite={!!(can.minutesAdd || can.minutesEdit)}
                                     canDelete={!!can.minutesDelete} />
            )}

            {/* EVERY SHOP DRAWING ON THIS ACTIVITY AND ITS STATE (owner,
                2026-10-08: "make sure every draweer per activity on the
                schedule also lists all shop drawings of that area/activity and
                their state (delivered, pending ...)").
                ABOVE the checklist, because whether the sheets have arrived is
                what decides if this activity can be signed off at all - and
                the component prints the refusal the sign-off would give. */}
            <h4 style={{ marginBottom: 6 }}>Shop drawings</h4>
            <ActivityShopDrawings projectId={projectId} ganttItemId={selected._id} />

            <h4 style={{ marginBottom: 6, marginTop: 14 }}>Checklist</h4>
            {(selected.checklist || []).length === 0 ? (
              <div style={{ color: "#999", marginBottom: 16 }}>No checklist items</div>
            ) : (
              <ul style={{ paddingLeft: 18, marginBottom: 16 }}>
                {selected.checklist.map((c, i) => (
                  <li
                    key={i}
                    style={{
                      textDecoration: c.done ? "line-through" : "none",
                      color: c.done ? "#999" : "#333",
                    }}
                  >
                    {c.label}
                  </li>
                ))}
              </ul>
            )}

            <h4 style={{ marginBottom: 6 }}>
              Issues ({drawerIssues.length})
            </h4>
            {drawerIssues.length === 0 ? (
              <div style={{ color: "#999" }}>No issues tagged to this item</div>
            ) : (
              drawerIssues.map((i) => (
                <div
                  key={i._id}
                  style={{ padding: "6px 0", borderBottom: "1px solid #f5f5f5" }}
                >
                  <a onClick={() => issueLink(i)}>
                    {i.displayId ? `${issueLabel(i)} — ` : ""}
                    {i.title}
                  </a>
                  <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                    <Tag>{i.status}</Tag>
                    {i.priority && (
                      <Tag color={PRIORITY_COLORS[i.priority] || "default"}>{i.priority}</Tag>
                    )}
                    {i.dueDate && `due ${fmtDate(i.dueDate)}`}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
